/**
 * question-source.ts — bank fetch + difficulty-aware sampling for the entry.
 *
 * Pure: only fetches JSON, uses Fisher-Yates with a tiny seeded PRNG so test
 * cases can pin output. NEVER imports overlay/sound modules.
 *
 * Critical schema reminder (`docs/G3S2_ENRICHMENT_SPEC.md`):
 *   difficulty is a question ROOT-LEVEL field. Always read q.difficulty,
 *   NEVER q.metadata.difficulty.
 */

import { getBankUrl } from './bank-lookup';

/**
 * Lightweight subset of GeneratedQuestion that the entry quiz UI needs.
 *
 * v1.1 — discriminated union over `kind`:
 *   • 'mcq'              → existing multiple-choice
 *   • 'fill'             → existing fill-in-blank (incl. 是非題 ○/×)
 *   • 'worked-example'   → new schema-building scaffold (CLT, see
 *                          docs/spec/WORKED_EXAMPLE_V1_SPEC.md). No
 *                          correctAnswer / options; carries steps[] +
 *                          finalAnswer. Always difficulty=1; chapter-onset.
 *
 * Schema additive — existing mcq / fill banks parse unchanged. Loaders
 * (samplePractice / sampleChallenge / sampleAdaptive) handle the new kind
 * naturally because they only inspect `q.difficulty` / `q.surfaceVariant`.
 */
export type EntryQuestion =
  | {
      kind: 'mcq';
      prompt: string;
      options: string[];
      /** mcq → index into options */
      correctAnswer: number;
      explanation?: string;
      kp: string;
      surfaceVariant?: string;
      difficulty?: 1 | 2 | 3;
      id?: string;
      /**
       * 題組 id(2026-06-11):同 group 的題在題庫檔內**相鄰**且依賴前題的
       * 表/情境(如「承上題」)。抽樣必須整組進整組出、組內保序 —
       * 用 sampleGroupedPractice,別用裸 shuffle。
       */
      group?: string;
    }
  | {
      kind: 'fill';
      prompt: string;
      /** fill → expected answer string */
      correctAnswer: string;
      acceptedAnswers?: string[];
      explanation?: string;
      kp: string;
      surfaceVariant?: string;
      difficulty?: 1 | 2 | 3;
      id?: string;
      /** 題組 id — 見 mcq 分支說明。 */
      group?: string;
      /** Allow stray `options` ignored at parse time (older banks). */
      options?: string[];
    }
  | {
      kind: 'worked-example';
      prompt: string;
      steps: { text: string; visual?: string }[];
      finalAnswer: string;
      followUp?: string;
      kp: string;
      surfaceVariant: string;
      difficulty: 1;
      id?: string;
      /** 題組 id — 見 mcq 分支說明。 */
      group?: string;
      /**
       * v1.1 — phantom fields kept as `never` to ease migration. Existing
       * mcq/fill code paths that touched `q.correctAnswer` / `q.explanation`
       * / `q.options` / `q.acceptedAnswers` will get `undefined` at runtime
       * when accidentally fed a worked-example (which they never should — WE
       * is gated at learn-quiz render entry). The `?` marks them optional so
       * union access does not break narrowing.
       */
      correctAnswer?: never;
      explanation?: never;
      options?: never;
      acceptedAnswers?: never;
    };

/**
 * v1.1 — Type guards. Use in code paths that legitimately work only on
 * answerable questions (sampler / paper / mistake-log / result-card) to
 * filter out `worked-example` before reading `correctAnswer` / etc.
 */
export function isWorkedExample(
  q: EntryQuestion,
): q is Extract<EntryQuestion, { kind: 'worked-example' }> {
  return q.kind === 'worked-example';
}

export function isAnswerable(
  q: EntryQuestion,
): q is Extract<EntryQuestion, { kind: 'mcq' | 'fill' }> {
  return q.kind === 'mcq' || q.kind === 'fill';
}

interface BankJson {
  kp: string;
  textbook?: string;
  grade?: number;
  semester?: number;
  questions: EntryQuestion[];
}

const bankCache = new Map<string, BankJson>();

export async function loadBankFor(kpId: string): Promise<BankJson | null> {
  const url = getBankUrl(kpId);
  if (!url) return null;
  if (bankCache.has(url)) return bankCache.get(url)!;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Failed to load bank ${url}: ${resp.status}`);
  const bank = (await resp.json()) as BankJson;
  bankCache.set(url, bank);
  return bank;
}

/** Mulberry32 — deterministic PRNG for repeatable shuffles. */
export function makeRng(seed: number | string): () => number {
  let s = typeof seed === 'number' ? seed | 0 : hashSeed(seed);
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashSeed(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function shuffle<T>(list: T[], rng: () => number): T[] {
  const out = list.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export interface SamplePlan {
  /** Picked questions in display order. */
  questions: EntryQuestion[];
  /** True if we had to fill with lower-difficulty questions (challenge mode). */
  fallbackUsed: boolean;
  /** How many true d3 questions exist in the bank (for the intro page). */
  d3Count: number;
}

/**
 * Sample `take` questions, preferring difficulty===3, falling back to d2 then
 * the rest if d3 stock is exhausted. surfaceVariant dedup is honoured.
 */
export function sampleChallenge(
  bank: BankJson,
  take: number,
  seed: number | string,
): SamplePlan {
  const rng = makeRng(seed);
  const d3 = bank.questions.filter((q) => q.difficulty === 3);
  const d3Count = d3.length;
  const picked: EntryQuestion[] = [];
  const usedVariants = new Set<string>();

  const pushFrom = (pool: EntryQuestion[]): void => {
    for (const q of shuffle(pool, rng)) {
      if (picked.length >= take) break;
      const v = q.surfaceVariant ?? q.prompt;
      if (usedVariants.has(v)) continue;
      usedVariants.add(v);
      picked.push(q);
    }
  };
  pushFrom(d3);
  let fallbackUsed = picked.length < take;
  if (picked.length < take) {
    const d2 = bank.questions.filter((q) => q.difficulty === 2);
    pushFrom(d2);
  }
  if (picked.length < take) {
    const others = bank.questions.filter((q) => q.difficulty !== 2 && q.difficulty !== 3);
    pushFrom(others);
    fallbackUsed = true;
  }
  // Final safety: if dedup left us short, just pad allowing dupes.
  if (picked.length < take) {
    for (const q of shuffle(bank.questions, rng)) {
      if (picked.length >= take) break;
      if (!picked.includes(q)) picked.push(q);
    }
  }
  return { questions: picked.slice(0, take), fallbackUsed, d3Count };
}

/** Vanilla sample: shuffle full bank, take N with surfaceVariant dedup. */
export function samplePractice(
  bank: BankJson,
  take: number,
  seed: number | string,
): EntryQuestion[] {
  const rng = makeRng(seed);
  const shuffled = shuffle(bank.questions, rng);
  const used = new Set<string>();
  const out: EntryQuestion[] = [];
  for (const q of shuffled) {
    if (out.length >= take) break;
    const v = q.surfaceVariant ?? q.prompt;
    if (used.has(v)) continue;
    used.add(v);
    out.push(q);
  }
  // pad with dupes if undersized
  if (out.length < take) {
    for (const q of shuffled) {
      if (out.length >= take) break;
      if (!out.includes(q)) out.push(q);
    }
  }
  return out;
}

/**
 * 把題庫切成「抽樣單位」:同 `group` 的**相鄰**題合成一個單位(保序),
 * 其餘單題各自一個單位。題組 = 一張表/情境 + 連續子題(如「承上題」),
 * 抽樣切斷它,後面的子題就無法作答(boss 2026-06-11)。
 */
export function toSampleUnits(questions: EntryQuestion[]): EntryQuestion[][] {
  const units: EntryQuestion[][] = [];
  let current: EntryQuestion[] = [];
  let currentGroup: string | null = null;
  for (const q of questions) {
    const g = q.group ?? null;
    if (g !== null && g === currentGroup) {
      current.push(q);
      continue;
    }
    if (current.length > 0) units.push(current);
    current = [q];
    currentGroup = g;
  }
  if (current.length > 0) units.push(current);
  return units;
}

/**
 * 題組安全版抽題(出 A4 練習卷用):以「單位」洗牌 —— 題組整組進整組出、
 * 組內順序保留;塞不下的題組整組跳過(不切斷),寧可題數略少。
 * Pure、同 seed 可重現,介面同 samplePractice。
 */
export function sampleGroupedPractice(
  bank: BankJson,
  take: number,
  seed: number | string,
): EntryQuestion[] {
  const rng = makeRng(seed);
  const units = shuffle(toSampleUnits(bank.questions), rng);
  const used = new Set<string>();
  const out: EntryQuestion[] = [];
  for (const unit of units) {
    if (out.length >= take) break;
    const key = unit.map((q) => q.surfaceVariant ?? q.prompt).join('§');
    if (used.has(key)) continue;
    used.add(key);
    // 題組塞不下 → 整組跳過找下一單位;單題照常受 take 限制。
    if (unit.length > 1 && out.length + unit.length > take) continue;
    out.push(...unit);
  }
  return out;
}

/**
 * D-001 Phase B — Adaptive-difficulty-aware sampler for 練習模式.
 *
 * Prefers questions matching `preferredDifficulty` (1|2|3); if that pool is
 * undersized falls back to neighbour difficulties (closest-first), then
 * everything else, with `surfaceVariant` dedup applied throughout.
 *
 * Pure: deterministic given the same seed + bank + preferredDifficulty.
 * **Does NOT consult progress.adaptiveDifficultyState** — caller passes the
 * resolved difficulty in (`getRecommendedDifficulty` 在 learn-quiz 那一層讀).
 *
 * Only used when `isAdaptiveEnabled(mode)` is true — formal exam / challenge
 * still use `sampleChallenge` / `samplePractice` per Boss D3.
 *
 * Spec: SKILL_LEVEL_DESIGN.md §4.4 (出題 pool 對接).
 */
export function sampleAdaptive(
  bank: BankJson,
  take: number,
  seed: number | string,
  preferredDifficulty: 1 | 2 | 3,
): EntryQuestion[] {
  const rng = makeRng(seed);
  const used = new Set<string>();
  const out: EntryQuestion[] = [];

  const tryPushFrom = (pool: EntryQuestion[]): void => {
    for (const q of shuffle(pool, rng)) {
      if (out.length >= take) break;
      const v = q.surfaceVariant ?? q.prompt;
      if (used.has(v)) continue;
      used.add(v);
      out.push(q);
    }
  };

  // Closest-first fallback order: prefer exact, then ±1, then the remaining
  // tier. e.g. preferred=2 → [d2, d1, d3]; preferred=1 → [d1, d2, d3];
  // preferred=3 → [d3, d2, d1]. d3-first for preferredDifficulty=3 keeps
  // 卷王 feel for kids that earned it.
  const order: Array<1 | 2 | 3> =
    preferredDifficulty === 2 ? [2, 1, 3]
    : preferredDifficulty === 1 ? [1, 2, 3]
    : [3, 2, 1];

  for (const tier of order) {
    if (out.length >= take) break;
    tryPushFrom(bank.questions.filter((q) => q.difficulty === tier));
  }

  // Final fallback: questions WITHOUT difficulty metadata (unknown tier).
  if (out.length < take) {
    tryPushFrom(bank.questions.filter((q) => q.difficulty !== 1 && q.difficulty !== 2 && q.difficulty !== 3));
  }
  // Safety pad: allow dupes if dedup left us short (rare; tiny banks).
  if (out.length < take) {
    for (const q of shuffle(bank.questions, rng)) {
      if (out.length >= take) break;
      if (!out.includes(q)) out.push(q);
    }
  }
  return out.slice(0, take);
}

/** Difficulty count summary for chapter-page display ("暖 N / 中 N / 挑 N *"). */
export interface DifficultyBreakdown {
  total: number;
  d1: number;
  d2: number;
  d3: number;
  unknown: number;
  /** True when ≥ 80% of questions lack `difficulty` — UI should show '*' note. */
  needsAsterisk: boolean;
}

export function difficultyBreakdown(bank: BankJson | null): DifficultyBreakdown {
  if (!bank) return { total: 0, d1: 0, d2: 0, d3: 0, unknown: 0, needsAsterisk: false };
  let d1 = 0, d2 = 0, d3 = 0, unknown = 0;
  for (const q of bank.questions) {
    if (q.difficulty === 1) d1++;
    else if (q.difficulty === 2) d2++;
    else if (q.difficulty === 3) d3++;
    else unknown++;
  }
  const total = bank.questions.length;
  const needsAsterisk = total > 0 && unknown / total >= 0.8;
  return { total, d1, d2, d3, unknown, needsAsterisk };
}
